import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Heading } from "@chakra-ui/react";
import { styled } from "styled-components";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout as StripeEmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { loadStripe, StripeEmbeddedCheckoutOptions } from "@stripe/stripe-js";
import { STRIPE_PUBLISHABLE_KEY } from "../constants";
import { useUrlParams } from "../hooks/useUrlParams";
import { useViewport } from "../hooks/useViewport";
import { Button } from "../components/Button";
import { colors } from "../colors";
import { GetCampaignResponse, getCampaign } from "../api/campaign/getCampaign";

const PageWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background-color: ${colors.white};
  padding: ${(props) => (props.$isMobile ? "0" : "2rem")};
  width: 100%;
`;

const Header = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  max-width: ${(props) => (props.$isMobile ? "100%" : "800px")};
  margin-bottom: ${(props) => (props.$isMobile ? "0.5rem" : "2rem")};
  padding: ${(props) => (props.$isMobile ? "1rem" : "0")};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: ${(props) => (props.$isMobile ? "wrap" : "nowrap")};
  gap: 1rem;
  background-color: ${colors.white};
`;

const CheckoutContainer = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  max-width: ${(props) => (props.$isMobile ? "100%" : "800px")};
  padding: ${(props) => (props.$isMobile ? "0" : "0")};
`;

const SpinnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 4rem;
`;

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

export const Checkout = () => {
  const { queryParams, routeParams } = useUrlParams();
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const [campaignDetails, setCampaignDetails] = useState<
    GetCampaignResponse | undefined
  >(undefined);
  const clientSecret = queryParams.clientSecret;

  useEffect(() => {
    if (routeParams.id) {
      getCampaign(routeParams.id).then((campaign) =>
        setCampaignDetails(campaign)
      );
    }
  }, [routeParams.id]);

  if (!clientSecret) {
    navigate(`/campaign/${routeParams.id}`);
    return null;
  }

  const handleBack = () => {
    navigate(`/campaign/${routeParams.id}`);
  };

  if (!campaignDetails) {
    return (
      <PageWrapper $isMobile={isMobile}>
        <SpinnerContainer>
          <Spinner size="xl" />
        </SpinnerContainer>
      </PageWrapper>
    );
  }

  const options: StripeEmbeddedCheckoutOptions = {
    clientSecret,
    onComplete: () => {
      console.log("Checkout completed");
    },
  };

  return (
    <PageWrapper $isMobile={isMobile}>
      <Header $isMobile={isMobile}>
        <Button
          buttonType="secondary"
          label="← Back"
          type="button"
          onClick={handleBack}
        />
        <Heading size={isMobile ? "md" : "lg"}>{campaignDetails.name}</Heading>
      </Header>
      <CheckoutContainer $isMobile={isMobile}>
        <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
          <StripeEmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </CheckoutContainer>
    </PageWrapper>
  );
};
